import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  IconButton,
  Paper,
  Modal,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

const DynamicFields = ({ dynamicData = [], onChange }) => {
  const [openModal, setOpenModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // null means add mode
  const [newField, setNewField] = useState({ key: "", values: [""] });

  const handleSaveField = () => {
    if (!newField.key || newField.values.every((v) => v.trim() === "")) return;

    const valueArray = newField.values.filter((v) => v.trim() !== "");
    let updated;

    if (editIndex !== null) {
      // Edit existing
      updated = [...dynamicData];
      updated[editIndex] = { key: newField.key, values: valueArray };
    } else {
      // Add new
      updated = [...dynamicData, { key: newField.key, values: valueArray }];
    }

    onChange && onChange(updated);
    resetModal();
  };

  const handleEditField = (index) => {
    const fieldToEdit = dynamicData[index];
    setNewField({ key: fieldToEdit.key, values: [...fieldToEdit.values] });
    setEditIndex(index);
    setOpenModal(true);
  };

  const handleDeleteField = (indexToRemove) => {
    const updated = dynamicData.filter((_, i) => i !== indexToRemove);
    onChange && onChange(updated);
  };

  const resetModal = () => {
    setNewField({ key: "", values: [""] });
    setEditIndex(null);
    setOpenModal(false);
  };

  const handleValueChange = (index, newValue) => {
    const updatedValues = [...newField.values];
    updatedValues[index] = newValue;
    setNewField({ ...newField, values: updatedValues });
  };

  const addNewValueInput = () => {
    setNewField((prev) => ({ ...prev, values: [...prev.values, ""] }));
  };

  const removeValueInput = (indexToRemove) => {
    const updatedValues = newField.values.filter((_, i) => i !== indexToRemove);
    setNewField({ ...newField, values: updatedValues });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>
        Dynamic Fields
      </Typography>

      {dynamicData?.map((item, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton onClick={() => handleEditField(index)} size="small">
              ✏️
            </IconButton>
            <IconButton onClick={() => handleDeleteField(index)} size="small">
              <Delete color="error" />
            </IconButton>
          </Box>
          <Typography fontWeight="bold">{item.key}</Typography>
          <Box mt={1}>
            {item?.values?.map((line, i) => (
              <Typography key={i} variant="body2" sx={{ ml: 1 }}>
                • {line}
              </Typography>
            ))}
          </Box>
        </Paper>
      ))}

      <Button startIcon={<Add />} onClick={() => setOpenModal(true)}>
        Add Field
      </Button>

      <Modal open={openModal} onClose={resetModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" mb={2}>
            {editIndex !== null ? "Edit Field" : "Add New Field"}
          </Typography>

          <TextField
            label="Key"
            value={newField.key}
            onChange={(e) =>
              setNewField((prev) => ({ ...prev, key: e.target.value }))
            }
            fullWidth
            margin="normal"
          />

          <Typography variant="subtitle2" mt={2} mb={1}>
            Answers
          </Typography>

          {newField?.values?.map((val, i) => (
            <Box key={i} display="flex" alignItems="center" gap={1} mb={1}>
              <TextField
                value={val}
                onChange={(e) => handleValueChange(i, e.target.value)}
                fullWidth
                placeholder={`Answer ${i + 1}`}
              />
              {newField?.values?.length > 1 && (
                <IconButton onClick={() => removeValueInput(i)} size="small">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}

          <Button onClick={addNewValueInput} size="small" sx={{ mt: 1 }}>
            + Add Another Answer
          </Button>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={resetModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveField}
              disabled={
                !newField.key || newField.values.every((v) => v.trim() === "")
              }
            >
              {editIndex !== null ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DynamicFields;
